import React, {useContext, lazy, Suspense} from 'react';
import {Outlet, Route, Routes, Navigate, useParams, useLocation} from 'react-router-dom';

import {UserContext} from '-/contexts/User';
import {hasFeature} from '-/features';
import {isDev} from '-/firebase';

import {hasPermission} from '-/data/utils';
import {canEditGearCheckTemplates, canEditStationCheckTemplates} from '-/data/checkPermissions';
import {SECTION_KEYS as SKILL_DRILL_SECTION_KEYS, STANDALONE_SECTION_KEYS as STANDALONE_SKILL_DRILL_SECTION_KEYS} from '-/data/skillDrills';
import {gearDrawerRoutePaths, gearRouteDescriptors} from '-/gearDepth';

import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

// Lazy load all page components
const Login = lazy(() => import('-/pages/Login'));
const NotFound = lazy(() => import('-/pages/NotFound'));
const Manage = lazy(() => import('-/pages/Manage'));
const Search = lazy(() => import('-/pages/Search'));

const Privacy = lazy(() => import('-/pages/Privacy'));
const Terms = lazy(() => import('-/pages/Terms'));

const History = lazy(() => import('-/pages/History'));

const Settings = lazy(() => import('-/pages/Settings'));

const Help = lazy(() => import('-/pages/Help'));

const Dashboard = lazy(() => import('-/pages/Dashboard'));
const Onboarding = lazy(() => import('-/pages/Onboarding'));

const Incidents = lazy(() => import('-/pages/Incidents'));
const Incident = lazy(() => import('-/pages/Incident'));
const Reports = lazy(() => import('-/pages/Reports'));
const Ask = lazy(() => import('-/pages/Ask'));
const ImportIncidents = lazy(() => import('-/pages/incidents/Import'));

const Members = lazy(() => import('-/pages/Members'));
const Member = lazy(() => import('-/pages/member'));
const QualificationMatrix = lazy(() => import('-/pages/QualificationMatrix'));
const TrainingMatrix = lazy(() => import('-/pages/TrainingMatrix'));

const Stations = lazy(() => import('-/pages/Stations'));
const Station = lazy(() => import('-/pages/Station'));
const EditStations = lazy(() => import('-/pages/stations/Edit'));
const LegacyStationsRedirect = lazy(() => import('-/pages/settings/Stations'));

const Apparatuses = lazy(() => import('-/pages/Apparatuses'));
const Apparatus = lazy(() => import('-/pages/Apparatus'));
const EditApparatus = lazy(() => import('-/pages/apparatuses/Edit'));

const Fuel = lazy(() => import('-/pages/Fuel'));
const FuelTank = lazy(() => import('-/pages/FuelTank'));

const Pretrip = lazy(() => import('-/pages/apparatuses/Pretrip'));

const Check = lazy(() => import('-/pages/Check'));
const EditChecks = lazy(() => import('-/pages/checks/Edit'));

const HostedTrainingRecord = lazy(() => import('-/pages/HostedTrainingRecord'));
const RegionalTraining = lazy(() => import('-/pages/RegionalTraining'));
const Calendar = lazy(() => import('-/pages/Calendar'));
const RegionalCalendar = lazy(() => import('-/pages/RegionalCalendar'));

const Payroll = lazy(() => import('-/pages/Payroll'));
const Workzoom = lazy(() => import('-/pages/Workzoom'));
const PayrollPeriodDetail = lazy(() => import('-/pages/payroll/PayrollPeriodDetail'));
const AdminHoursTimesheets = lazy(() => import('-/pages/adminHours/AdminHoursTimesheets'));
const AdminHoursTimesheet = lazy(() => import('-/pages/adminHours/AdminHoursTimesheet'));

const Tasks = lazy(() => import('-/pages/Tasks'));

const Trainings = lazy(() => import('-/pages/Trainings'));
const Training = lazy(() => import('-/pages/Training'));
const ImportTraining = lazy(() => import('-/pages/trainings/Import'));
const TrainingKeywords = lazy(() => import('-/pages/trainings/Keywords'));

const LessonPlans = lazy(() => import('-/pages/LessonPlans'));
const LessonPlan = lazy(() => import('-/pages/LessonPlan'));

const Requirements = lazy(() => import('-/pages/Requirements'));
const Requirement = lazy(() => import('-/pages/Requirement'));
const SkillDrillSection = lazy(() => import('-/pages/skillDrills/Section'));
const DriverHoursSection = lazy(() => import('-/pages/driverHours/Section'));
const AssignmentsList = lazy(() => import('-/pages/Assignments'));
const AssignmentRunner = lazy(() => import('-/pages/assignment/Runner'));

const PublicEducations = lazy(() => import('-/pages/PublicEducations'));
const PublicEducation = lazy(() => import('-/pages/PublicEducation'));

const DutyShifts = lazy(() => import('-/pages/DutyShifts'));

const Preplans = lazy(() => import('-/pages/Preplans'));
const Preplan = lazy(() => import('-/pages/Preplan'));
const PreplansMap = lazy(() => import('-/pages/preplans/Map'));

const BurnPermits = lazy(() => import('-/pages/BurnPermits'));
const OhsMeetings = lazy(() => import('-/pages/OhsMeetings'));
const OhsMeeting = lazy(() => import('-/pages/OhsMeeting'));

const Files = lazy(() => import('-/pages/Files'));
const FileCategory = lazy(() => import('-/pages/FileCategory'));

const DeploymentResources = lazy(() => import('-/pages/DeploymentResources'));
const HowToFillDtr = lazy(() => import('-/pages/deployment-resources/HowToFillDtr'));
const Deployments = lazy(() => import('-/pages/Deployments'));
const Deployment = lazy(() => import('-/pages/Deployment'));

const SkillSheet = lazy(() => import('-/pages/SkillSheet'));

const NFPA = lazy(() => import('-/pages/NFPA'));

// /requirements/driving[/tab] -> /requirements/driverClearance[/tab] (#1580 rename). Kept
// because the approver digest links to the log tab by absolute URL.
const DrivingRedirect = () => {
    const {tab} = useParams();
    return <Navigate to={['/requirements/driverClearance', tab].filter(Boolean).join('/')} replace />;
};

const NFPARedirect = () => {
    const {standard, edition} = useParams();
    const path = ['/training-standards', standard, edition].filter(Boolean).join('/');
    return <Navigate to={path} replace />;
};

const FR = lazy(() => import('-/pages/quizzes/FR'));
const Renewal = lazy(() => import('-/pages/quizzes/fr/Renewal'));
const FRStatistics = lazy(() => import('-/pages/quizzes/fr/Statistics'));

const Health = lazy(() => import('-/pages/Health'));

const UI = lazy(() => import('-/pages/UI'));
const UploadShowcase = lazy(() => import('-/pages/design/UploadShowcase'));
const CheckShowcase = lazy(() => import('-/pages/design/CheckShowcase'));

const Gear = lazy(() => import('-/pages/Gear'));
const GearCollection = lazy(() => import('-/pages/GearCollection'));
const GearShell = lazy(() => import('-/pages/gear/redesign/GearShell'));
const GearAudits = lazy(() => import('-/pages/gear/audits/GearAudits'));
const GearView = lazy(() => import('-/pages/gear/redesign/GearView'));
const LocationView = lazy(() => import('-/pages/gear/redesign/LocationView'));
const ItemDrawerRoute = lazy(() => import('-/pages/gear/redesign/ItemDrawerRoute'));

// Loading component for Suspense fallback
const LoadingFallback = () => (
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px'}}>
        <CircularProgress />
    </Box>
);

const TaskRedirect = () => {
    const {taskId} = useParams();
    return <Navigate to={`/tasks?task=${taskId}`} replace />;
};

const GearTaskRedirect = () => {
    const {gearId, taskId} = useParams();
    return <Navigate to={`/gear/${gearId}?task=${taskId}`} replace />;
};

const GearItemRedirect = () => {
    const {catId, itemId} = useParams();
    return <Navigate to={`/gear/${catId}/items/${itemId}`} replace />;
};

// On a direct hit to an item URL (no backgroundLocation), redirect to the same
// URL with backgroundLocation pointing at the parent category. The main
// <Routes> then renders the category page underneath and the secondary
// <Routes> mounts the drawer over it — same UX as clicking into the item.
const ItemRouteRedirect = () => {
    const location = useLocation();
    if (location.state?.backgroundLocation) return null;
    const parentPathname = location.pathname.replace(/\/items\/[^/]+\/?$/, '');
    return (
        <Navigate
            to={{pathname: location.pathname, search: location.search}}
            state={{backgroundLocation: {pathname: parentPathname, search: ''}}}
            replace
        />
    );
};

const GearRecordRedirect = () => {
    const {catId, itemId, recordId} = useParams();
    return <Navigate to={`/gear/${catId}/items/${itemId}?record=${recordId}`} replace />;
};

const RedirectWithParams = ({toTemplate}) => {
    const params = useParams();
    const segments = toTemplate.split('/');

    const updatedSegments = segments.map(segment => {
        if (segment.startsWith(':')) {
            const paramKey = segment.slice(1);
            return params[paramKey] || segment;
        }
        
        return segment;
    });

    const to = `/${updatedSegments.join('/')}`;

    return <Navigate to={to} replace />;
};

const Router = function() {
    const {currentUser} = useContext(UserContext);
    const {isStation} = currentUser || {};

    const reRouteIfAuthenticated = destination => {
        // An operator or hub hand-off (/login#hubToken=…) replaces whoever is
        // signed in, so it must reach Login rather than bounce to the dashboard
        // as the previous account (#2307). Login strips the fragment first thing.
        if (currentUser && !/(?:^#|&)(operatorToken|hubToken)=/.test(window.location.hash || '')) {
            return <Navigate to="/" />;
        }

        return destination;
    };

    const reRouteIfUnauthenticated = destination => {
        if (!currentUser) {
            return <Navigate to="/login" />;
        }

        return destination;
    };

    // Item drawer routes match the same param shape as the gear item routes so
    // useParams() inside the drawer resolves to the same path the full-page
    // GearItem would see (this is what makes TasksDialog attach tasks to the
    // item rather than the parent category). The path math lives in gearDepth.js
    // so it's testable without React — see gear/routerDepth.test.js.
    const generateGearDrawerRoutes = () => gearDrawerRoutePaths().map(path => (
        <Route key={`drawer-${path}`} path={path} element={<ItemDrawerRoute />} />
    ));

    const generateGearRoutes = () => {
        // The template editor is gated the same way the apparatus one is below
        // (`editChecks.write`), plus the gear ladder's own Approve rung — see
        // data/checkPermissions.js. Dropping the descriptor rather than
        // rendering a blocked page mirrors apparatus: the URL falls through to
        // NotFound.
        const canEditTemplates = canEditGearCheckTemplates(currentUser);

        // Convert route descriptors to <Route /> components
        return gearRouteDescriptors().filter(route => canEditTemplates || route.element !== 'editChecks').map(route => {
            const {path, element: elementString, isRedirect} = route;

            let element = elementString === 'collection' ? <GearCollection /> : <ItemRouteRedirect />;
            if (elementString === 'editChecks') {
                element = <EditChecks />;
            } else if (elementString === 'check') {
                element = <Check />;
            } else if (isRedirect) {
                element = <RedirectWithParams toTemplate={isRedirect} />;
            }

            return (
                <Route
                    key={path}
                    path={path}
                    element={element}
                />
            );
        });
    };

    // const isSuperAdmin = currentUser && currentUser.superadmin === true;

    // Background-location pattern: when a row link sets
    // {state: {backgroundLocation: location}}, the main <Routes> keeps rendering
    // the page the user was on (the "background") while a second <Routes>
    // matches the new URL and renders the drawer over it. This is the standard
    // React Router modal-route trick (no remount of the underlying page).
    const location = useLocation();
    const background = location.state?.backgroundLocation;

    return (
        <Suspense fallback={<LoadingFallback />}>
            <Routes location={background || location}>
                <Route path="/login" element={reRouteIfAuthenticated(<Login />)} />
                {/* Not redirected when signed in: emailed action links (verify
                    email, undo a 2-step verification change) are usually opened in
                    the browser that's already signed in (#2155). */}
                <Route path="/manage" element={<Manage />} />

                {/* Public legal pages — reachable without an account (a board can be
                    pointed at a canonical copy). Static paths outrank the "*" catch-all. */}
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />

                <Route path="*" element={reRouteIfUnauthenticated(<NotFound />)} />

                {currentUser && (
                    <Route path="*">
                        <Route index element={<Dashboard />} />

                        {/* Global search (#1715). Ungated on purpose: the endpoint
                            behind it filters every result type by the caller's own
                            permissions, so a member with none simply gets an empty
                            page rather than a 404 that hides that search exists. */}
                        <Route path="search" element={<Search />} />
                        {/* Onboarding requests (docs/architecture/onboarding-requests.md)
                            — admin-only like the collection, and only while the
                            operator has it switched on for this department. */}
                        {hasFeature('onboardingRequests') && hasPermission(currentUser, 'admin') && (
                            <Route path="onboarding" element={<Onboarding />} />
                        )}
                        <Route path="help" element={<Help />} />

                        {currentUser.superadmin === true && (
                            <Route path="health" element={<Health />} />
                        )}

                        {isDev && (
                            <Route path="ui" element={<UI />} />
                        )}

                        {isDev && (
                            <Route path="ui/uploads" element={<UploadShowcase />} />
                        )}

                        {isDev && (
                            <Route path="ui/checks" element={<CheckShowcase />} />
                        )}

                        {!isStation && (
                            <>
                                <Route path="profile" element={<Member isProfile />} />
                                {/* #1718 — a member's own service record. No permission
                                    gate: the panel is read-only without users.write. */}
                                <Route path="profile/serviceHistory" element={<Member isProfile activeTab="serviceHistory" />} />
                                <Route path="profile/qualifications" element={<Member isProfile activeTab="qualifications" />} />
                                {hasFeature('requirements') && <Route path="profile/assignments" element={<Member isProfile activeTab="assignments" />} />}
                                {/* Driving hours (#1580) — the member-facing half. No permission
                                    gate on the self route, same as profile/assignments: a member
                                    logs their own hours and the api refuses anything else. */}
                                {hasFeature('requirements') && <Route path="profile/driverClearance" element={<Member isProfile activeTab="driverClearance" />} />}
                                <Route path="profile/transcript" element={<Member isProfile activeTab="transcript" />} />
                                {hasPermission(currentUser, 'training.read') && <Route path="profile/training" element={<Member isProfile activeTab="training" />} />}
                                {hasPermission(currentUser, 'incidents.read') && <Route path="profile/incidents" element={<Member isProfile activeTab="incidents" />} />}
                                <Route path="profile/gear" element={<Member isProfile activeTab="gear" />} />
                                {/* No deployments.read gate on the self route: the ladder
                                    gives a firefighter none, and this is their own tour of
                                    duty. The api applies the same rule
                                    (api/src/routes/memberDeployments.js). */}
                                {hasFeature('deployments') && <Route path="profile/deployments" element={<Member isProfile activeTab="deployments" />} />}
                                {/* #1482: no permission gate on the self route — the api
                                    filters confidential records out of a self-view. */}
                                {hasFeature('hr') && <Route path="profile/records" element={<Member isProfile activeTab="records" />} />}
                                {hasPermission(currentUser, 'tasks.write') && <Route path="profile/tasks" element={<Member isProfile activeTab="tasks" />} />}
                                <Route path="profile/history" element={<Member isProfile activeTab="history" />} />
                            </>
                        )}

                        {hasPermission(currentUser, 'users.write') && (
                            <Route path="profile/jibcConsent" element={<Member isProfile activeTab="jibcConsent" />} />
                        )}

                        {hasPermission(currentUser, 'apparatus.read') && (
                            <Route path="apparatus">
                                <Route index element={<Apparatuses />} />
                                <Route path="add" element={<EditApparatus />} />

                                <Route path=":id">
                                    <Route index element={<Apparatus />} />
                                    <Route path="edit" element={<EditApparatus />} />
                                    <Route path=":type" element={<Navigate to=".." replace />} />

                                    <Route path="pretrips/:checkId" element={<Pretrip />} />
                                    <Route path=":type/:checkId" element={<Check />} />

                                    {hasPermission(currentUser, 'editChecks.write') && (
                                        <>
                                            <Route path=":type/edit" element={<EditChecks />} />
                                        </>
                                    )}
                                </Route>
                            </Route>
                        )}

                        {hasFeature('fuel') && hasPermission(currentUser, 'fuel.read') && (
                            <Route path="fuel">
                                <Route index element={<Fuel />} />
                                <Route path=":id" element={<FuelTank />} />
                            </Route>
                        )}

                        <Route path="matrix" element={<Navigate to="/" replace />} />
                        {hasFeature('qualificationMatrix') && (
                            <Route path="matrix/qualification" element={<QualificationMatrix />} />
                        )}
                        <Route path="matrix/training" element={<TrainingMatrix />} />

                        {hasFeature('calendar') && (
                            <><Route path="calendar" element={<Calendar />} />
                                <Route path="regional-training" element={<RegionalCalendar />} />
                                <Route path="regional-training-records/:id" element={<HostedTrainingRecord />} /><Route path="regional-training/:id" element={<RegionalTraining />} /></>
                        )}

                        <Route path="quizzes">
                            <Route index element={<Navigate to="/quizzes/fr" replace />} />
                            <Route path="fr" element={<FR />} />
                            <Route path="fr/renewal" element={<Renewal />} />
                            <Route path="fr/statistics" element={<FRStatistics />} />
                        </Route>

                        {hasFeature('trainingStandards') && (
                            <>
                                <Route path="training-standards" element={<NFPA />} />
                                <Route path="training-standards/:standard" element={<NFPA />} />
                                <Route path="training-standards/:standard/:edition" element={<NFPA />} />
                                <Route path="nfpa" element={<Navigate to="/training-standards" replace />} />
                                <Route path="nfpa/:standard" element={<NFPARedirect />} />
                                <Route path="nfpa/:standard/:edition" element={<NFPARedirect />} />
                            </>
                        )}

                        {hasPermission(currentUser, 'gear.read') && (
                            <Route path="gear" element={<GearShell />}>
                                <Route index element={<Gear />} />
                                <Route path="views/:viewId" element={<GearView />} />
                                <Route path="audits" element={<GearAudits />} />
                                <Route path="audits/:auditId" element={<GearAudits />} />
                                <Route path="locations/apparatus/:apparatusId" element={<LocationView />} />
                                <Route path="locations/:locationId" element={<LocationView />} />
                                <Route path=":gearId/tasks/:taskId" element={<GearTaskRedirect />} />
                                <Route path=":catId/:itemId/records/:recordId" element={<GearRecordRedirect />} />
                                <Route path=":catId/:itemId" element={<GearItemRedirect />} />
                                {generateGearRoutes()}
                            </Route>
                        )}
                                                
                        {hasPermission(currentUser, 'incidents.read') && (
                            <Route path="incidents">
                                <Route index element={<Incidents />} />

                                {hasPermission(currentUser, 'incidents.write') && (
                                    <>
                                        <Route path="add" element={<Incident />} />
                                        <Route path=":id" element={<Incident />} />
                                        <Route path=":id/:tab" element={<Incident />} />
                                        <Route path="import" element={<ImportIncidents />} />
                                    </>
                                )}
                            </Route>
                        )}

                        {hasPermission(currentUser, 'reports.read') && (
                            <Route path="reports">
                                <Route index element={<Reports />} />
                                <Route path=":reportId" element={<Reports />} />
                            </Route>
                        )}

                        {/* Not behind the `ask` feature: that only hides the menu
                            entry, so the page can be tried before it's switched on. */}
                        {hasPermission(currentUser, 'reports.read') && (
                            <Route path="ask" element={<Ask />} />
                        )}

                        <Route path="jibcSkills">
                            <Route index element={<SkillSheet />} />
                        </Route>

                        {(hasPermission(currentUser, 'training.stats') || hasPermission(currentUser, 'training.read')) && (
                            <Route path="training">
                                <Route index element={<Trainings />} />

                                {hasPermission(currentUser, 'training.write') && (
                                    <>
                                        <Route path="add" element={<Training />} />
                                        <Route path=":id" element={<Training />} />
                                        <Route path=":id/:tab" element={<Training />} />
                                        <Route path="import" element={<ImportTraining />} />
                                        <Route path="keywords" element={<TrainingKeywords />} />
                                    </>
                                )}
                            </Route>
                        )}

                        {hasFeature('lessonPlans') && hasPermission(currentUser, 'lessonplans.read') && (
                            <Route path="lessonplans">
                                <Route index element={<LessonPlans />} />

                                {hasPermission(currentUser, 'lessonplans.write') && (
                                    <Route path="add" element={<LessonPlan />} />
                                )}

                                <Route path=":id" element={<LessonPlan />}>
                                    <Route path=":tab" element={null} />
                                </Route>
                            </Route>
                        )}

                        {hasFeature('requirements') && (hasPermission(currentUser, 'requirements.read') || hasPermission(currentUser, 'requirements.write')) && (
                            <Route path="requirements">
                                <Route index element={<Requirements />} />

                                {hasPermission(currentUser, 'requirements.write') && (
                                    <Route path="add" element={<Requirement />} />
                                )}

                                {/* Skill-drill sections — clean URLs (/requirements/exterior,
                                    /requirements/driverOperator, …). Static section keys outrank the
                                    :id route, so they never collide with an authored requirement.
                                    Routed from the section KEYS rather than the resolved sections, so
                                    the route exists regardless of training provider — the Requirements
                                    list is what hides a section a department doesn't use (#1579). */}
                                {(hasPermission(currentUser, 'requirements.report') || hasPermission(currentUser, 'requirements.approve')) &&
                                    [...SKILL_DRILL_SECTION_KEYS, ...STANDALONE_SKILL_DRILL_SECTION_KEYS].map(key => (
                                        <Route key={key} path={key} element={<SkillDrillSection sectionKey={key} />}>
                                            <Route path=":tab" element={null} />
                                        </Route>
                                    ))}

                                {/* Driving hours (#1580) — its own row in the Requirements list,
                                    not a tab on Driver/Operator: the check sheet is a per-item
                                    PASS/retest evaluation, this is a running per-rig total with
                                    an approval workflow. Static, so it outranks :id below, and
                                    permission-gated the same way a skill-drill section is. The
                                    page itself explains itself when the department tracks no
                                    hours, so the route exists regardless of the setting. */}
                                {(hasPermission(currentUser, 'requirements.report') || hasPermission(currentUser, 'requirements.approve')) && (
                                    <>
                                        <Route path="driverClearance" element={<DriverHoursSection />}>
                                            <Route path=":tab" element={null} />
                                        </Route>
                                        {/* Renamed from "Driving Hours" once clearance became an
                                            officer's decision rather than something the hours
                                            derived. Approver emails and bookmarks point at the old
                                            path, so it redirects rather than 404s. */}
                                        <Route path="driving" element={<DrivingRedirect />}>
                                            <Route path=":tab" element={null} />
                                        </Route>
                                    </>
                                )}

                                <Route path=":id" element={<Requirement />}>
                                    <Route path=":tab" element={null} />
                                </Route>
                            </Route>
                        )}

                        {hasFeature('requirements') && !isStation && (
                            <Route path="assignments">
                                <Route index element={<AssignmentsList />} />
                                <Route path=":id" element={<AssignmentRunner />} />
                            </Route>
                        )}

                        <Route path="publicEducation/:id" element={<RedirectWithParams toTemplate="public-education/:id" />} />

                        {hasFeature('publicEducation') && hasPermission(currentUser, 'publicEducation.read') && (
                            <Route path="public-education">
                                <Route index element={<PublicEducations />} />

                                {hasPermission(currentUser, 'publicEducation.write') && (
                                    <>
                                        <Route path="add" element={<PublicEducation />} />
                                        <Route path=":id" element={<PublicEducation />} />
                                        <Route path=":id/:tab" element={<PublicEducation />} />
                                    </>
                                )}
                            </Route>
                        )}

                        {hasFeature('dutyShifts') && hasPermission(currentUser, 'dutyShifts.read') && (
                            <Route path="dutyshifts">
                                {/* add/:id render the list with the editor drawer open over it */}
                                <Route index element={<DutyShifts />} />

                                {hasPermission(currentUser, 'dutyShifts.write') && (
                                    <>
                                        <Route path="add" element={<DutyShifts />} />
                                        <Route path=":id" element={<DutyShifts />} />
                                    </>
                                )}
                            </Route>
                        )}

                        {hasFeature('preplans') && hasPermission(currentUser, 'preplans.read') && (
                            <Route path="preplans">
                                <Route index element={<Preplans />} />
                                <Route path="map" element={<PreplansMap />} />

                                {hasPermission(currentUser, 'preplans.write') && (
                                    <>
                                        <Route path="add" element={<Preplan />} />
                                        <Route path=":id" element={<Preplan />} />
                                        <Route path=":id/:tab" element={<Preplan />} />
                                    </>
                                )}
                            </Route>
                        )}

                        {hasFeature('burnPermits') && hasPermission(currentUser, 'burnPermits.read') && (
                            <Route path="burn-permits" element={<BurnPermits />} />
                        )}

                        {/* OH&S meetings (#2295). A meeting opens read-only without ohs.write.
                            A recorded meeting stays reachable with the feature off: it carries
                            paid hours, every link to it (search, calendar, approval tasks) and
                            the /training/:id redirect land here, and it may still need approving. */}
                        {hasPermission(currentUser, 'ohs.read') && (
                            <Route path="ohs">
                                {hasFeature('ohs') && <Route index element={<OhsMeetings />} />}
                                {hasFeature('ohs') && hasPermission(currentUser, 'ohs.write') && <Route path="add" element={<OhsMeeting />} />}
                                <Route path=":id" element={<OhsMeeting />} />
                            </Route>
                        )}

                        {hasFeature('files') && hasPermission(currentUser, 'files.read') && (
                            <Route path="files">
                                <Route index element={<Files />} />
                                <Route path=":categorySlug" element={<FileCategory />} />
                                <Route path=":categorySlug/folder/:folderId" element={<FileCategory />} />
                            </Route>
                        )}

                        {hasFeature('deployments') && hasPermission(currentUser, 'deployments.read') && (
                            <Route path="deployments">
                                <Route index element={<Deployments />} />
                                <Route path="resources" element={<DeploymentResources />} />
                                <Route path="resources/how-to-fill-dtr" element={<HowToFillDtr />} />

                                {hasPermission(currentUser, 'deployments.write') && (
                                    <>
                                        <Route path="add" element={<Deployment />} />
                                        {/* Single route with an optional :tab so switching tabs only
                                            updates params — two separate :id and :id/:tab routes made
                                            React Router remount <Deployment/> (and reset the form,
                                            losing unsaved rotations) when crossing between them. */}
                                        <Route path=":id/:tab?" element={<Deployment />} />
                                    </>
                                )}
                            </Route>
                        )}

                        <Route path="stations">
                            <Route index element={<Stations redirectSingleStation={!background} />} />
                            <Route path=":id" element={<Station />} />
                            {hasPermission(currentUser, 'stations.write') && (
                                <>
                                    <Route path="add" element={<EditStations createOnly />} />
                                    <Route path="manage" element={<EditStations />} />
                                    <Route path=":id/edit" element={<Station editing />} />
                                </>
                            )}

                            {/* Hall checks (#1920). Mirrors the apparatus block:
                                running one needs the check claim, authoring the
                                template needs editChecks.write, and an unmatched
                                :type falls back to the station page. */}
                            {hasPermission(currentUser, 'stationChecks.read') && (
                                <Route path=":id">
                                    <Route path=":type/:checkId" element={<Check />} />
                                    {canEditStationCheckTemplates(currentUser) && (
                                        <Route path=":type/edit" element={<EditChecks />} />
                                    )}
                                </Route>
                            )}
                        </Route>

                        {hasFeature('payroll') && hasPermission(currentUser, 'payroll.read') && (
                            <>
                                <Route path="payroll" element={<Payroll />} />
                                <Route path="payroll/:periodId" element={<PayrollPeriodDetail />} />
                            </>
                        )}

                        {/* Open to any signed-in member, deliberately NOT gated on
                            payroll.submitHours/payroll.write. An officer can log
                            hours on a member's behalf (#1376) or add them to a
                            shared roster (#1441), and payroll.submitHours is off by
                            default — so gating here left those members unable to
                            open a sheet filed in their name, while the "logged for
                            you" email deep-linked them straight to it. Nothing
                            leaks: the list queries only sheets whose roster
                            contains the caller, and firestore.rules enforces the
                            same on every read. Creating a sheet still needs a
                            permission — see AdminHoursTimesheets. */}
                        <Route path="admin-hours" element={<AdminHoursTimesheets />} />
                        <Route path="admin-hours/:timesheetId" element={<AdminHoursTimesheet />} />

                        {/* A member's own hours, laid out for RDCO's Workzoom app. No
                            permission: they're your own records (the api checks
                            payroll.read before showing anyone else's). Not for a
                            station login, which isn't a person who gets paid. */}
                        {hasFeature('workzoom') && !isStation && (
                            <Route path="workzoom" element={<Workzoom />} />
                        )}

                        {hasPermission(currentUser, 'admin') && (
                            <>
                                <Route path="settings" element={<Settings />} />
                                <Route path="settings/stations" element={<LegacyStationsRedirect />} />
                                <Route path="settings/:tab" element={<Settings />} />
                                <Route path="history">
                                    <Route index element={<History />} />
                                </Route>
                            </>
                        )}

                        <Route path="tasks" element={<Tasks />} />
                        <Route path="tasks/:taskId" element={<TaskRedirect />} />

                        {hasPermission(currentUser, 'users.write') && (
                            <>
                                <Route path="users">
                                    <Route path=":id" element={<Member />} />
                                    <Route path=":id/serviceHistory" element={<Member activeTab="serviceHistory" />} />
                                    <Route path=":id/qualifications" element={<Member activeTab="qualifications" />} />
                                    {hasFeature('requirements') && (hasPermission(currentUser, 'requirements.read') || hasPermission(currentUser, 'requirements.report')) && <Route path=":id/assignments" element={<Member activeTab="assignments" />} />}
                                    {hasFeature('requirements') && (hasPermission(currentUser, 'requirements.read') || hasPermission(currentUser, 'requirements.report')) && <Route path=":id/driverClearance" element={<Member activeTab="driverClearance" />} />}
                                    {hasPermission(currentUser, 'training.read') && <Route path=":id/training" element={<Member activeTab="training" />} />}
                                    {hasPermission(currentUser, 'incidents.read') && <Route path=":id/incidents" element={<Member activeTab="incidents" />} />}
                                    {hasPermission(currentUser, 'gear.read') && <Route path=":id/gear" element={<Member activeTab="gear" />} />}
                                    {hasFeature('deployments') && hasPermission(currentUser, 'deployments.read') && <Route path=":id/deployments" element={<Member activeTab="deployments" />} />}
                                    {hasFeature('hr') && hasPermission(currentUser, 'hr.read') && <Route path=":id/records" element={<Member activeTab="records" />} />}
                                    <Route path=":id/history" element={<Member activeTab="history" />} />
                                    <Route path=":id/transcript" element={<Member activeTab="transcript" />} />
                                    <Route path=":id/jibcConsent" element={<Member activeTab="jibcConsent" />} />
                                    {hasPermission(currentUser, 'admin') && <Route path=":id/permissions" element={<Member activeTab="permissions" />} />}
                                    {hasPermission(currentUser, 'tasks.write') && <Route path=":id/tasks" element={<Member activeTab="tasks" />} />}
                                    <Route index element={<Members />} />
                                    <Route path="add" element={<Member />} />
                                </Route>
                            </>
                        )}

                    </Route>
                )}
            </Routes>

            {background && currentUser && (
                // Drawer routes get their own Suspense boundary with a null
                // fallback. Sharing the parent Suspense meant the first lazy
                // load of ItemDrawerRoute suspended the entire tree, blanking
                // the background category page until the chunk arrived. With a
                // sibling boundary the background stays mounted; the drawer
                // just appears once it loads.
                <Suspense fallback={null}>
                    <Routes>
                        {hasPermission(currentUser, 'gear.read') && <Route path="gear" element={<Outlet />}>
                            {generateGearDrawerRoutes()}
                        </Route>}
                        {hasPermission(currentUser, 'stations.write') && (
                            <Route path="stations/add" element={<EditStations createOnly overlayOnly />} />
                        )}
                    </Routes>
                </Suspense>
            )}
        </Suspense>
    );
};

export default Router;
